
import React from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlusCircle, DollarSign } from "lucide-react";
import { DebtItem as DebtItemType, PayoffMethod } from './debtCalculatorTypes';
import DebtItem from './DebtItem';
import { formatCurrency } from './debtCalculatorUtils';

interface DebtFormProps {
  debts: DebtItemType[];
  monthlyPayment: number;
  payoffMethod: PayoffMethod;
  setMonthlyPayment: (value: number) => void;
  setPayoffMethod: (value: PayoffMethod) => void;
  updateDebt: (id: string, field: keyof DebtItemType, value: string | number) => void;
  removeDebt: (id: string) => void;
  addDebt: () => void;
  calculatePayoff: () => void;
}

const DebtForm: React.FC<DebtFormProps> = ({
  debts,
  monthlyPayment,
  payoffMethod,
  setMonthlyPayment,
  setPayoffMethod,
  updateDebt,
  removeDebt,
  addDebt,
  calculatePayoff
}) => {
  const totalDebt = debts.reduce((sum, debt) => sum + debt.balance, 0);
  const totalMinPayment = debts.reduce((sum, debt) => sum + debt.minPayment, 0);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Enter Your Debts</h3>
        
        <div className="space-y-4">
          {debts.map((debt, index) => (
            <DebtItem 
              key={debt.id}
              debt={debt}
              index={index}
              updateDebt={updateDebt}
              removeDebt={removeDebt}
            />
          ))}
          
          <Button variant="outline" onClick={addDebt} className="w-full">
            <PlusCircle className="mr-2 h-4 w-4" /> Add Another Debt
          </Button>
        </div>
      </div>
      
      <div className="bg-muted p-4 rounded-md">
        <div className="text-lg font-semibold mb-2">Total Debt: {formatCurrency(totalDebt)}</div>
        <div className="text-sm text-muted-foreground">
          Total minimum payments: {formatCurrency(totalMinPayment)} /month
        </div>
      </div>
      
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="monthlyPayment">Monthly Payment Amount ($)</Label>
          <Input
            id="monthlyPayment"
            type="number"
            value={monthlyPayment}
            onChange={(e) => setMonthlyPayment(Number(e.target.value))}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="payoffMethod">Payoff Method</Label>
          <Select value={payoffMethod} onValueChange={(value: PayoffMethod) => setPayoffMethod(value)}>
            <SelectTrigger id="payoffMethod">
              <SelectValue placeholder="Select method" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="avalanche">
                Debt Avalanche (Highest Interest First)
              </SelectItem>
              <SelectItem value="snowball">
                Debt Snowball (Lowest Balance First)
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <Button onClick={calculatePayoff} className="w-full">
        <DollarSign className="mr-2 h-4 w-4" /> Calculate Debt Payoff Plan
      </Button>
    </div>
  );
};

export default DebtForm;
